import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { TenantsAPI } from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save } from "lucide-react";

const ALL_MODULES = [
  "admissions","attendance","fees","notices","homework","library",
  "curriculum","gradebook","timetable","transport","hostel","hr",
  "procurement","maintenance","finance","analytics","mobile",
  "accreditation","sso",
];

export default function TenantDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuper = user?.roles?.includes("super_admin");

  const { data: tenant, isLoading } = useQuery({
    queryKey: ["tenant", id],
    queryFn: () => TenantsAPI.get(id),
  });

  const [name, setName] = useState("");
  const [plan, setPlan] = useState("trial");
  const [primaryColor, setPrimaryColor] = useState("#002FA7");
  const [shortName, setShortName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [modules, setModules] = useState(new Set());

  useEffect(() => {
    if (tenant) {
      setName(tenant.name);
      setPlan(tenant.plan);
      setPrimaryColor(tenant.branding?.primary_color || "#002FA7");
      setShortName(tenant.branding?.short_name || "");
      setLogoUrl(tenant.branding?.logo_url || "");
      setModules(new Set(tenant.modules_enabled || []));
    }
  }, [tenant]);

  const saveBranding = useMutation({
    mutationFn: () =>
      TenantsAPI.update(id, {
        name,
        branding: { primary_color: primaryColor, short_name: shortName, logo_url: logoUrl },
      }),
    onSuccess: () => {
      toast.success("Branding saved");
      queryClient.invalidateQueries({ queryKey: ["tenant", id] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const savePlan = useMutation({
    mutationFn: () => TenantsAPI.update(id, { plan }),
    onSuccess: () => {
      toast.success("Plan updated");
      queryClient.invalidateQueries({ queryKey: ["tenant", id] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const saveModules = useMutation({
    mutationFn: () => TenantsAPI.update(id, { modules_enabled: Array.from(modules) }),
    onSuccess: () => {
      toast.success("Modules updated");
      queryClient.invalidateQueries({ queryKey: ["tenant", id] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const toggleModule = (m) => {
    const next = new Set(modules);
    if (next.has(m)) next.delete(m); else next.add(m);
    setModules(next);
  };

  if (isLoading) {
    return <AppShell><div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div></AppShell>;
  }
  if (!tenant) {
    return <AppShell><div className="p-10 text-sm text-muted-foreground">Tenant not found.</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="p-8 max-w-5xl" data-testid="tenant-detail-page">
        <Link to="/tenants" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="h-3 w-3" /> Tenants
        </Link>
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-[0.25em] mb-2">
              /tenants/{tenant.slug}
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl flex items-center gap-3">
              {tenant.name}
              <Badge variant={tenant.is_active ? "default" : "outline"}>
                {tenant.is_active ? "Active" : "Inactive"}
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground mt-2 font-mono">
              {tenant.slug} · {tenant.plan.toUpperCase()}
            </p>
          </div>
        </div>

        <Tabs defaultValue="branding" data-testid="tenant-tabs">
          <TabsList>
            <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules">Modules</TabsTrigger>
            <TabsTrigger value="plan" disabled={!isSuper} data-testid="tab-plan">Plan</TabsTrigger>
          </TabsList>

          {/* Branding */}
          <TabsContent value="branding" className="mt-6">
            <div className="border border-border rounded-md bg-card p-6 max-w-2xl">
              <h3 className="font-display font-bold text-lg mb-1">Brand identity</h3>
              <p className="text-sm text-muted-foreground mb-6">
                What students &amp; staff see in this tenant&apos;s app.
              </p>
              <div className="space-y-4">
                <FormField label="Display name">
                  <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="branding-name" />
                </FormField>
                <FormField label="Short name (≤6 chars)">
                  <Input
                    value={shortName}
                    onChange={(e) => setShortName(e.target.value.slice(0, 6))}
                    placeholder="WBPS"
                    className="font-mono uppercase"
                    data-testid="branding-short-name"
                  />
                </FormField>
                <FormField label="Logo URL">
                  <Input
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://…/logo.png"
                    data-testid="branding-logo-url"
                  />
                </FormField>
                <FormField label="Primary color">
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-10 w-14 rounded border border-border cursor-pointer"
                      data-testid="branding-color"
                    />
                    <Input
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="font-mono"
                    />
                    <div className="flex-1 h-10 rounded-md border border-border" style={{ background: primaryColor }} />
                  </div>
                </FormField>
                <Button onClick={() => saveBranding.mutate()} disabled={saveBranding.isPending} data-testid="branding-save">
                  {saveBranding.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save branding
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Modules */}
          <TabsContent value="modules" className="mt-6">
            <div className="border border-border rounded-md bg-card p-6">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 className="font-display font-bold text-lg">Enabled modules</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Toggle which platform capabilities are visible to this tenant&apos;s staff.
                  </p>
                </div>
                <Button onClick={() => saveModules.mutate()} disabled={saveModules.isPending} data-testid="modules-save">
                  {saveModules.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save ({modules.size})
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {ALL_MODULES.map((m) => (
                  <label
                    key={m}
                    className="flex items-center gap-2 border border-border rounded-md px-3 py-2 cursor-pointer hover:bg-secondary/60 transition"
                    data-testid={`module-row-${m}`}
                  >
                    <Checkbox
                      checked={modules.has(m)}
                      onCheckedChange={() => toggleModule(m)}
                      data-testid={`module-toggle-${m}`}
                    />
                    <span className="text-sm capitalize">{m}</span>
                  </label>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Plan */}
          <TabsContent value="plan" className="mt-6">
            <div className="border border-border rounded-md bg-card p-6 max-w-md">
              <h3 className="font-display font-bold text-lg mb-1">Subscription plan</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Only platform super-admins can change tenant plans.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {["trial", "starter", "pro", "enterprise"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPlan(p)}
                    className={`border rounded-md px-3 py-2 text-left text-sm transition ${
                      plan === p ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"
                    }`}
                    data-testid={`plan-option-${p}`}
                  >
                    <div className="font-semibold capitalize">{p}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                      {p === "trial" ? "30 days" : p === "enterprise" ? "custom" : "monthly"}
                    </div>
                  </button>
                ))}
              </div>
              <Button onClick={() => savePlan.mutate()} disabled={savePlan.isPending || !isSuper} data-testid="plan-save">
                {savePlan.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Update plan
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
